import type { Breadcrumb, ErrorEvent } from '@sentry/angular'

/**
 * FR-059 AC-4.2 — the web half of the Sentry masking. Pure functions so they are
 * unit-testable; `main.ts` wires them as `beforeSend` (after its drops) and
 * `beforeBreadcrumb`.
 *
 * Masks every email address in free text (`jane@example.com` → `***@example.com`;
 * the domain is kept, it helps debugging and is not personal) in the exception
 * values, message, extra, contexts, tags and every breadcrumb's message + data —
 * on top of the user scrub that was here before (email / username / ip_address).
 *
 * Parity: the web cannot import API code, so EMAIL_PATTERN is a local copy of the
 * one in apps/api/app/services/logging/mask.ts — change both together.
 */

/**
 * Parity: apps/api/app/services/logging/mask.ts EMAIL_PATTERN.
 * Every quantifier is BOUNDED (RFC 5321 limits: local part ≤ 64, label ≤ 63) so
 * the scan stays linear on long unbroken runs (base64url, hex, JWTs). The
 * unbounded `[…]+@` form backtracked the whole run at each start: 100k chars ≈ 4 s.
 */
const EMAIL_PATTERN = /[A-Za-z0-9._%+-]{1,64}@([A-Za-z0-9-]{1,63}(?:\.[A-Za-z0-9-]{1,63})*\.[A-Za-z]{2,63})/g

/**
 * FR-059 U5c F2 — parity: apps/api/app/services/logging/mask.ts
 * ENCODED_EMAIL_PATTERN. An address whose `@` is percent-encoded
 * (`jane%40example.com`), as it sits in a URL PATH or a route name: EMAIL_PATTERN
 * needs a literal `@`. Same bounds (linear scan). Masked to `***%40<domain>`.
 * U5c R2-1: the local part is literal local-part characters or the escapes of
 * `+ - . _` (`%2B %2D %2E %5F`) only, so any other escape ends it —
 * `mailto%3Ajane%40example.com` keeps `mailto%3A` and masks `jane`. Each step
 * is a literal char or one of four fixed escapes (no overlap), so the scan stays
 * bounded and linear. The lookbehind stops a match from starting inside an
 * escape (`%3Ajane` must not match from `3Ajane`).
 */
const ENCODED_EMAIL_PATTERN =
  /(?<!%[0-9A-F]?)(?:[A-Za-z0-9._+-]|%(?:2[BDE]|5F)){1,64}%40([A-Za-z0-9-]{1,63}(?:\.[A-Za-z0-9-]{1,63})*\.[A-Za-z]{2,63})/gi

const MAX_DEPTH = 10

/**
 * FR-059 U8 (A1-G1) — parity: apps/api/start/sentry.ts REDACT_KEYS
 * (`token|key|secret|password|email`, substring, case-insensitive), plus
 * `signature`. A query value under such a key is replaced whole with
 * `[Filtered]`, as on the API: a reset / invite `?token=` is a credential, not
 * an email, so the email masks alone never touched it.
 */
const REDACT_KEYS = /token|key|secret|password|signature|email/i
const FILTERED = '[Filtered]'

/**
 * Every email address in `s` masked to `***@<domain>`, and every
 * percent-encoded one to `***%40<domain>` (U5c F2).
 */
export function maskEmails(s: string): string {
  return s.replace(EMAIL_PATTERN, '***@$1').replace(ENCODED_EMAIL_PATTERN, '***%40$1')
}

/** Deep, cycle-safe, depth-bounded copy of `v` with every string through maskEmails. */
export function maskEmailsDeep(v: unknown): unknown {
  return walk(v, new WeakSet(), 0)
}

function walk(v: unknown, seen: WeakSet<object>, depth: number): unknown {
  if (typeof v === 'string') return maskEmails(v)
  if (v === null || typeof v !== 'object') return v
  if (seen.has(v)) return '[Circular]'
  if (depth >= MAX_DEPTH) return '[Truncated]'
  seen.add(v)
  try {
    if (v instanceof Error) {
      return { name: v.name, message: maskEmails(v.message), stack: maskEmails(v.stack ?? '') }
    }
    if (Array.isArray(v)) return v.map((item) => walk(item, seen, depth + 1))
    const out: Record<string, unknown> = {}
    for (const [k, value] of Object.entries(v as Record<string, unknown>)) {
      out[k] = walk(value, seen, depth + 1)
    }
    return out
  } finally {
    seen.delete(v)
  }
}

/**
 * A URL with every email masked, query values included — those arrive
 * percent-encoded (`jane%40example.com`), which the plain pattern cannot see,
 * so each query value is decoded, masked and re-encoded. A value under a
 * secret key (REDACT_KEYS) becomes `[Filtered]` (U8 A1-G1).
 *
 * FR-059 U5b F10 (parity with the api's redactQueryString): each RAW
 * `&`-segment is masked first, BEFORE URLSearchParams decodes a raw `+` into a
 * space — otherwise `jane.plus+tag@example.com` reaches maskEmails as
 * `jane.plus tag@example.com` and the `jane.plus` prefix leaks.
 */
export function maskUrl(url: string): string {
  const q = url.indexOf('?')
  if (q === -1) return maskEmails(url)
  const hash = url.indexOf('#', q)
  const query = url
    .slice(q + 1, hash === -1 ? undefined : hash)
    .split('&')
    .map(maskEmails)
    .join('&')
  const params = new URLSearchParams(query)
  let changed = false
  for (const [key, value] of Array.from(params.entries())) {
    const masked = REDACT_KEYS.test(key) ? FILTERED : maskEmails(value)
    if (masked !== value) {
      params.set(key, masked)
      changed = true
    }
  }
  const head = maskEmails(url.slice(0, q))
  const tail = hash === -1 ? '' : maskEmails(url.slice(hash))
  return `${head}?${changed ? params.toString() : query}${tail}`
}

/** Breadcrumb data keys whose string value is a URL: xhr/fetch `url`, navigation `from` / `to`. */
const URL_DATA_KEYS = ['url', 'from', 'to'] as const

/**
 * `beforeBreadcrumb`: masks the message, the URL-valued data (`data.url` and a
 * navigation breadcrumb's `data.from` / `data.to`: query values included, secret
 * ones filtered, U8 A1-G1) and every other data value — `data.arguments` holds a
 * console breadcrumb's logged values.
 */
export function scrubBreadcrumb(breadcrumb: Breadcrumb): Breadcrumb {
  const out: Breadcrumb = { ...breadcrumb }
  if (typeof out.message === 'string') out.message = maskEmails(out.message)
  if (out.data) {
    const data = maskEmailsDeep(out.data) as Record<string, unknown>
    for (const k of URL_DATA_KEYS) {
      const raw = out.data[k]
      if (typeof raw === 'string') data[k] = maskUrl(raw)
    }
    out.data = data
  }
  return out
}

/** Headers whose value is a URL by definition. */
const URL_HEADERS = new Set(['referer', 'origin'])
/** An absolute URL: `scheme://…`. */
const ABSOLUTE_URL = /^[a-z][a-z0-9+.-]*:\/\//i

/**
 * FR-059 U5b F9 (parity with apps/api/start/sentry.ts): the browser SDK sends
 * `Referer` (and `User-Agent`) as request headers. A URL-valued header goes
 * through maskUrl so a `%40` address in its query is caught; the rest through
 * maskEmails.
 */
function maskHeaders(headers: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(headers)) {
    if (typeof v !== 'string') {
      out[k] = v
      continue
    }
    const isUrl = URL_HEADERS.has(k.toLowerCase()) || ABSOLUTE_URL.test(v.trim())
    out[k] = isUrl ? maskUrl(v) : maskEmails(v)
  }
  return out
}

/**
 * The `beforeSend` scrub (runs after main.ts's drops): the user fields are
 * dropped as before, then every email in the free-form parts is masked —
 * the transaction name and request headers included (U5b F11 / F9).
 */
export function scrubEvent(event: ErrorEvent): ErrorEvent {
  if (event.user) {
    delete event.user.email
    delete event.user.username
    delete event.user.ip_address
  }
  for (const ex of event.exception?.values ?? []) {
    if (typeof ex.value === 'string') ex.value = maskEmails(ex.value)
  }
  if (typeof event.message === 'string') event.message = maskEmails(event.message)
  if (typeof event.transaction === 'string') event.transaction = maskEmails(event.transaction)
  if (event.request?.url) event.request.url = maskUrl(event.request.url)
  if (event.request?.headers) event.request.headers = maskHeaders(event.request.headers)
  if (event.extra) event.extra = maskEmailsDeep(event.extra) as ErrorEvent['extra']
  if (event.contexts) event.contexts = maskEmailsDeep(event.contexts) as ErrorEvent['contexts']
  if (event.tags) event.tags = maskEmailsDeep(event.tags) as ErrorEvent['tags']
  if (event.breadcrumbs) event.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb)
  return event
}
